/**
 * `AgenciesView` — ops2026 Agencies panel (founder request 2026-10-29).
 *
 * Staff pick an agency (search by name / account_id / domain / owner
 * email; only accounts with the `agency` toggle on). The right pane
 * shows managed clients + attributed signups and four actions:
 *
 *   - Add client             (POST  /agencies/:id/clients)
 *   - Remove client          (DELETE /agencies/:id/clients/:link_id)
 *   - Move client to agency  (POST  /agencies/:id/clients/:link_id/move)
 *   - Set / clear agency_of_record (PUT /accounts/:id/agency-of-record)
 *
 * Every action is behind an explicit confirm step with a required
 * free-text reason. The staff log carries the reason; the merchant
 * trail does NOT.
 *
 * Three accounts can share a name (there are three named "Vbout" in
 * production), so every row renders `account_id` + `owner_email` +
 * `primary_domain` to disambiguate.
 */

import { CircleDollarSign, RefreshCw, Search, UserPlus, UserMinus, Shuffle, Pin } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type {
  AdminAgenciesListResponse,
  AdminAgencyAttributedSignup,
  AdminAgencyDetailResponse,
  AdminAgencyListItem,
  AdminAgencyManagedClient,
} from '@sill/types/api';
import type { AgencyGrantedRole } from '@sill/types';

import type { AdminAccountSummary } from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { Pill } from '../../components/atoms/Pill.js';
import { formatIsoCompact } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';

import { AccountPicker } from './AccountPicker.js';

const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

const GRANTED_ROLES: readonly AgencyGrantedRole[] = ['admin', 'reviewer', 'viewer'];

type DialogKind =
  | { readonly kind: 'none' }
  | { readonly kind: 'add' }
  | { readonly kind: 'remove'; readonly client: AdminAgencyManagedClient }
  | { readonly kind: 'move'; readonly client: AdminAgencyManagedClient }
  | { readonly kind: 'aor_set'; readonly client_account_id: string; readonly client_label: string }
  | { readonly kind: 'aor_clear'; readonly client_account_id: string; readonly client_label: string }
  | { readonly kind: 'agency_pays'; readonly client: AdminAgencyManagedClient; readonly target: boolean };

export function AgenciesView(): JSX.Element {
  const C = useC();
  const api = useAdminApi();

  const [query, setQuery] = useState('');
  const [agencies, setAgencies] = useState<readonly AdminAgencyListItem[] | null>(null);
  const [agenciesError, setAgenciesError] = useState<string | null>(null);
  const [agenciesLoading, setAgenciesLoading] = useState(false);
  const [selectedAgencyId, setSelectedAgencyId] = useState<string | null>(null);

  const [detail, setDetail] = useState<AdminAgencyDetailResponse | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [dialog, setDialog] = useState<DialogKind>({ kind: 'none' });
  const [flash, setFlash] = useState<{ readonly tone: 'ok' | 'err'; readonly text: string } | null>(null);

  const loadAgencies = useCallback(
    async (q: string): Promise<void> => {
      setAgenciesLoading(true);
      setAgenciesError(null);
      try {
        const res: AdminAgenciesListResponse = await api.listAgencies({ q });
        setAgencies(res.agencies);
      } catch (err) {
        setAgenciesError(err instanceof ApiError ? err.message : 'Could not load agencies.');
      } finally {
        setAgenciesLoading(false);
      }
    },
    [api],
  );

  const loadDetail = useCallback(
    async (agencyAccountId: string): Promise<void> => {
      setDetailLoading(true);
      setDetailError(null);
      try {
        const res = await api.getAgencyDetail(agencyAccountId);
        setDetail(res);
      } catch (err) {
        if (err instanceof ApiError && err.kind === 'not_found') {
          setDetailError('Agency not found.');
        } else {
          setDetailError(err instanceof ApiError ? err.message : 'Could not load the agency detail.');
        }
      } finally {
        setDetailLoading(false);
      }
    },
    [api],
  );

  useEffect(() => {
    void loadAgencies('');
  }, [loadAgencies]);

  useEffect(() => {
    if (selectedAgencyId === null) {
      setDetail(null);
      return;
    }
    void loadDetail(selectedAgencyId);
  }, [selectedAgencyId, loadDetail]);

  const refreshAll = useCallback(async (): Promise<void> => {
    await loadAgencies(query);
    if (selectedAgencyId !== null) {
      await loadDetail(selectedAgencyId);
    }
  }, [loadAgencies, query, selectedAgencyId, loadDetail]);

  const onSearchKey = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === 'Enter') {
        void loadAgencies(query.trim());
      }
    },
    [loadAgencies, query],
  );

  const kicker = useMemo(
    () => ({
      fontFamily: monoFont,
      fontSize: 10,
      letterSpacing: 1.5,
      color: C.muted,
      textTransform: 'uppercase' as const,
    }),
    [C.muted],
  );

  return (
    <div style={{ padding: 24, maxWidth: 1400 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <div style={{ ...kicker, marginBottom: 8 }}>ops2026 · Agency management</div>
          <h1 style={{ fontFamily: serifFont, fontSize: 28, color: C.text, margin: 0 }}>Agencies</h1>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, marginTop: 4, maxWidth: 820, lineHeight: 1.5 }}>
            Add, remove or move the client accounts an agency manages. Every change is written to the client&apos;s audit log and the staff log; a free-text reason is required.
          </div>
        </div>
        <button
          type="button"
          onClick={() => void refreshAll()}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontFamily: sansFont,
            fontSize: 12,
            padding: '6px 12px',
            background: 'transparent',
            color: C.muted,
            border: `1px solid ${C.border}`,
            borderRadius: 3,
            cursor: 'pointer',
          }}
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {flash !== null ? (
        <Card
          style={{
            padding: 10,
            marginBottom: 12,
            background: flash.tone === 'ok' ? C.limeDim : C.surface,
            borderColor: flash.tone === 'ok' ? C.lime : C.coralFg,
          }}
        >
          <div style={{ fontFamily: sansFont, fontSize: 12.5, color: C.text }}>{flash.text}</div>
        </Card>
      ) : null}

      {/* Founder 2026-10-29 (mobile check): one column on phones, the
          picker beside the detail from md up. */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(280px,360px)_minmax(0,1fr)]">
        {/* Left column — agency picker */}
        <AgencyPicker
          query={query}
          onQueryChange={setQuery}
          onSearchKey={onSearchKey}
          onSearchClick={() => void loadAgencies(query.trim())}
          agencies={agencies}
          loading={agenciesLoading}
          error={agenciesError}
          selectedAgencyId={selectedAgencyId}
          onSelect={(id) => {
            setSelectedAgencyId(id);
            // On a phone the detail sits below the picker; bring it into view.
            if (typeof window !== 'undefined' && window.innerWidth < 768) {
              requestAnimationFrame(() => document.getElementById('agency-detail')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
            }
          }}
        />

        {/* Right column — detail */}
        <div id="agency-detail" style={{ minWidth: 0 }}>
          {selectedAgencyId === null ? (
            <Card style={{ padding: 24 }}>
              <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
                Pick an agency to see the clients it manages.
              </div>
            </Card>
          ) : (
            <AgencyDetail
              loading={detailLoading}
              error={detailError}
              detail={detail}
              onAddClient={() => setDialog({ kind: 'add' })}
              onRemoveClient={(client) => setDialog({ kind: 'remove', client })}
              onMoveClient={(client) => setDialog({ kind: 'move', client })}
              onSetAor={(id, label) =>
                setDialog({ kind: 'aor_set', client_account_id: id, client_label: label })
              }
              onClearAor={(id, label) =>
                setDialog({ kind: 'aor_clear', client_account_id: id, client_label: label })
              }
              onAgencyPays={(client, target) => setDialog({ kind: 'agency_pays', client, target })}
            />
          )}
        </div>
      </div>

      {dialog.kind === 'add' && selectedAgencyId !== null && detail !== null ? (
        <AddClientDialog
          agency={detail.agency}
          onClose={() => setDialog({ kind: 'none' })}
          onDone={(msg) => {
            setFlash({ tone: 'ok', text: msg });
            setDialog({ kind: 'none' });
            void loadDetail(selectedAgencyId);
          }}
          onError={(msg) => setFlash({ tone: 'err', text: msg })}
        />
      ) : null}
      {dialog.kind === 'remove' && selectedAgencyId !== null ? (
        <RemoveClientDialog
          agency_account_id={selectedAgencyId}
          client={dialog.client}
          onClose={() => setDialog({ kind: 'none' })}
          onDone={(msg) => {
            setFlash({ tone: 'ok', text: msg });
            setDialog({ kind: 'none' });
            void loadDetail(selectedAgencyId);
          }}
          onError={(msg) => setFlash({ tone: 'err', text: msg })}
        />
      ) : null}
      {dialog.kind === 'move' && selectedAgencyId !== null && agencies !== null ? (
        <MoveClientDialog
          from_agency_account_id={selectedAgencyId}
          client={dialog.client}
          agencies={agencies}
          onClose={() => setDialog({ kind: 'none' })}
          onDone={(msg) => {
            setFlash({ tone: 'ok', text: msg });
            setDialog({ kind: 'none' });
            void loadDetail(selectedAgencyId);
          }}
          onError={(msg) => setFlash({ tone: 'err', text: msg })}
        />
      ) : null}
      {dialog.kind === 'aor_set' && selectedAgencyId !== null ? (
        <SetAorDialog
          agency_account_id={selectedAgencyId}
          client_account_id={dialog.client_account_id}
          client_label={dialog.client_label}
          onClose={() => setDialog({ kind: 'none' })}
          onDone={(msg) => {
            setFlash({ tone: 'ok', text: msg });
            setDialog({ kind: 'none' });
            void loadDetail(selectedAgencyId);
          }}
          onError={(msg) => setFlash({ tone: 'err', text: msg })}
        />
      ) : null}
      {dialog.kind === 'aor_clear' && selectedAgencyId !== null ? (
        <ClearAorDialog
          client_account_id={dialog.client_account_id}
          client_label={dialog.client_label}
          onClose={() => setDialog({ kind: 'none' })}
          onDone={(msg) => {
            setFlash({ tone: 'ok', text: msg });
            setDialog({ kind: 'none' });
            void loadDetail(selectedAgencyId);
          }}
          onError={(msg) => setFlash({ tone: 'err', text: msg })}
        />
      ) : null}
      {dialog.kind === 'agency_pays' && selectedAgencyId !== null ? (
        <AgencyPaysDialog
          agency_account_id={selectedAgencyId}
          client={dialog.client}
          target={dialog.target}
          onClose={() => setDialog({ kind: 'none' })}
          onDone={(msg) => {
            setFlash({ tone: 'ok', text: msg });
            setDialog({ kind: 'none' });
            void loadDetail(selectedAgencyId);
          }}
          onError={(msg) => setFlash({ tone: 'err', text: msg })}
        />
      ) : null}
    </div>
  );
}

// ─── Left column — agency picker ────────────────────────────────────

interface AgencyPickerProps {
  query: string;
  onQueryChange: (v: string) => void;
  onSearchKey: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onSearchClick: () => void;
  agencies: readonly AdminAgencyListItem[] | null;
  loading: boolean;
  error: string | null;
  selectedAgencyId: string | null;
  onSelect: (account_id: string) => void;
}

function AgencyPicker(props: AgencyPickerProps): JSX.Element {
  const C = useC();
  const kicker = {
    fontFamily: monoFont,
    fontSize: 10,
    letterSpacing: 1.5,
    color: C.muted,
    textTransform: 'uppercase' as const,
  };

  return (
    <Card style={{ padding: 12, maxHeight: '72vh', overflowY: 'auto' }}>
      <div style={{ ...kicker, marginBottom: 8 }}>Agencies</div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        <input
          type="search"
          value={props.query}
          placeholder="Name, owner email, domain or account ID"
          onChange={(e) => props.onQueryChange(e.target.value)}
          onKeyDown={props.onSearchKey}
          style={{
            flex: 1,
            fontFamily: sansFont,
            fontSize: 12,
            padding: '6px 8px',
            background: C.bg,
            color: C.text,
            border: `1px solid ${C.border}`,
            borderRadius: 3,
            outline: 'none',
          }}
        />
        <button
          type="button"
          onClick={props.onSearchClick}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontFamily: sansFont,
            fontSize: 12,
            padding: '6px 8px',
            background: 'transparent',
            color: C.muted,
            border: `1px solid ${C.border}`,
            borderRadius: 3,
            cursor: 'pointer',
          }}
          aria-label="Search"
        >
          <Search size={12} />
        </button>
      </div>

      {props.error !== null ? (
        <div style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg }}>{props.error}</div>
      ) : null}

      {props.loading ? (
        <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>Loading…</div>
      ) : null}

      {props.agencies !== null && props.agencies.length === 0 ? (
        <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>No agencies match.</div>
      ) : null}

      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {(props.agencies ?? []).map((a) => {
          const active = a.account_id === props.selectedAgencyId;
          return (
            <li key={a.account_id} style={{ marginTop: 6 }}>
              <button
                type="button"
                onClick={() => props.onSelect(a.account_id)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  fontFamily: sansFont,
                  fontSize: 12.5,
                  padding: '8px 10px',
                  background: active ? C.surface2 : 'transparent',
                  color: C.text,
                  border: `1px solid ${active ? C.lime : C.border}`,
                  borderRadius: 3,
                  cursor: 'pointer',
                }}
                data-testid={`agency-pick-${a.account_id}`}
              >
                <div style={{ fontWeight: 600 }}>{a.name}</div>
                <div style={{ color: C.muted, fontFamily: monoFont, fontSize: 10, marginTop: 2 }}>
                  {a.account_id}
                </div>
                <div style={{ color: C.muted, fontSize: 11, marginTop: 2 }}>
                  {a.owner_email ?? '— no owner —'}
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                  {a.domains.slice(0, 2).map((d) => (
                    <Pill key={d} tone="neutral">{d}</Pill>
                  ))}
                  <Pill tone="sky">{a.active_clients} client{a.active_clients === 1 ? '' : 's'}</Pill>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

// ─── Right column — agency detail ──────────────────────────────────

interface AgencyDetailProps {
  loading: boolean;
  error: string | null;
  detail: AdminAgencyDetailResponse | null;
  onAddClient: () => void;
  onRemoveClient: (client: AdminAgencyManagedClient) => void;
  onMoveClient: (client: AdminAgencyManagedClient) => void;
  onSetAor: (client_account_id: string, client_label: string) => void;
  onClearAor: (client_account_id: string, client_label: string) => void;
  /** ADR-0263 §5.3 (S6) — flip agency-paid on an active link. `target`
   *  is the new value the staff member wants (true to turn on,
   *  false to turn off). */
  onAgencyPays: (client: AdminAgencyManagedClient, target: boolean) => void;
}

function AgencyDetail(props: AgencyDetailProps): JSX.Element {
  const C = useC();
  const kicker = {
    fontFamily: monoFont,
    fontSize: 10,
    letterSpacing: 1.5,
    color: C.muted,
    textTransform: 'uppercase' as const,
  };

  if (props.loading) {
    return (
      <Card style={{ padding: 24 }}>
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>Loading…</div>
      </Card>
    );
  }
  if (props.error !== null) {
    return (
      <Card style={{ padding: 16, borderColor: C.coralFg }}>
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg }}>{props.error}</div>
      </Card>
    );
  }
  if (props.detail === null) return <></>;
  const { agency, account_plan, clients, attributed_signups } = props.detail;

  const active_clients = clients.filter((c) => c.link_status === 'active');
  const other_clients = clients.filter((c) => c.link_status !== 'active');

  return (
    <>
      <Card style={{ padding: 16, marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ ...kicker, marginBottom: 6 }}>Selected agency</div>
            <div style={{ fontFamily: serifFont, fontSize: 22, color: C.text }}>{agency.name}</div>
            <div style={{ color: C.muted, fontFamily: monoFont, fontSize: 11, marginTop: 2 }}>
              {agency.account_id}
            </div>
            <div style={{ color: C.muted, fontSize: 12.5, marginTop: 4 }}>
              {agency.owner_email ?? '— no owner —'} · {agency.region.toUpperCase()}
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              {agency.domains.map((d) => (
                <Pill key={d} tone="neutral">{d}</Pill>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={props.onAddClient}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontFamily: sansFont,
              fontSize: 12,
              padding: '8px 12px',
              background: C.lime,
              color: C.ink,
              border: `1px solid ${C.lime}`,
              borderRadius: 3,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
            data-testid="agency-add-client"
          >
            <UserPlus size={12} /> Add client
          </button>
        </div>
      </Card>

      {/* ADR-0263 §2.6 (S6) — the agency's resolved v2 account plan. */}
      <AccountPlanCard account_plan={account_plan} />

      <Card style={{ padding: 16, marginBottom: 12 }}>
        <div style={{ ...kicker, marginBottom: 8 }}>Managed clients · {active_clients.length} active</div>
        {active_clients.length === 0 ? (
          <div style={{ fontFamily: sansFont, fontSize: 12.5, color: C.muted }}>
            No active clients. Use <em>Add client</em> to grant access.
          </div>
        ) : (
          <ClientTable
            rows={active_clients}
            onRemove={props.onRemoveClient}
            onMove={props.onMoveClient}
            onSetAor={props.onSetAor}
            onClearAor={props.onClearAor}
            onAgencyPays={props.onAgencyPays}
          />
        )}
      </Card>

      {other_clients.length > 0 ? (
        <Card style={{ padding: 16, marginBottom: 12 }}>
          <div style={{ ...kicker, marginBottom: 8 }}>History · {other_clients.length}</div>
          <ClientTable
            rows={other_clients}
            onRemove={props.onRemoveClient}
            onMove={props.onMoveClient}
            onSetAor={props.onSetAor}
            onClearAor={props.onClearAor}
            onAgencyPays={props.onAgencyPays}
            historical
          />
        </Card>
      ) : null}

      <Card style={{ padding: 16 }}>
        <div style={{ ...kicker, marginBottom: 8 }}>Credited signups · {attributed_signups.length}</div>
        <div style={{ fontFamily: sansFont, fontSize: 11.5, color: C.muted, marginBottom: 8 }}>
          Signups credited to this agency that it doesn&apos;t manage yet. Use Add client to give the agency access.
        </div>
        {attributed_signups.length === 0 ? (
          <div style={{ fontFamily: sansFont, fontSize: 12.5, color: C.muted }}>None.</div>
        ) : (
          <AttributedSignupsTable rows={attributed_signups} />
        )}
      </Card>
    </>
  );
}

interface ClientTableProps {
  rows: readonly AdminAgencyManagedClient[];
  onRemove: (c: AdminAgencyManagedClient) => void;
  onMove: (c: AdminAgencyManagedClient) => void;
  onSetAor: (client_account_id: string, label: string) => void;
  onClearAor: (client_account_id: string, label: string) => void;
  onAgencyPays: (c: AdminAgencyManagedClient, target: boolean) => void;
  historical?: boolean;
}

function ClientTable(props: ClientTableProps): JSX.Element {
  const C = useC();
  return (
    <>
    {/* Founder 2026-10-29 (mobile check): cards on phones so the actions
        are not hidden off the right edge of a scrolling table. */}
    <div className="md:hidden">
      {props.rows.map((r) => (
        <div key={r.link_id} data-testid="client-card" style={{ padding: '12px 0', borderBottom: `1px solid ${C.border}`, fontFamily: sansFont, fontSize: 12.5 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: C.text }}>{r.client_name}</div>
              <div style={{ color: C.muted, fontSize: 11, overflowWrap: 'anywhere' }}>
                {r.owner_email ?? '— no owner —'} · {r.primary_domain ?? 'no domain'}
              </div>
              <div style={{ color: C.muted, fontFamily: monoFont, fontSize: 10, overflowWrap: 'anywhere' }}>{r.client_account_id}</div>
            </div>
            <Pill tone={r.link_status === 'active' ? 'mint' : r.link_status === 'suspended' ? 'amber' : 'coral'}>{r.link_status}</Pill>
          </div>
          <div style={{ color: C.muted, fontSize: 11, marginTop: 6 }}>
            {r.granted_role} · linked {formatIsoCompact(r.created_at)}
            {r.is_agency_of_record ? ' · signup credited' : ''}
            {r.revoked_by_role !== null ? ` · removed by ${r.revoked_by_role}` : ''}
            {' · '}
            {r.link_status === 'active' && r.agency_pays_for_sites
              ? `agency pays (${r.client_live_sites} site${r.client_live_sites === 1 ? '' : 's'})`
              : 'client pays'}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
            <ClientActions row={r} {...props} />
          </div>
        </div>
      ))}
    </div>
    <div className="hidden md:block" style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', fontFamily: sansFont, fontSize: 12.5, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ color: C.muted, textAlign: 'left' }}>
            <th style={th(C.border)}>Client</th>
            <th style={th(C.border)}>Role</th>
            <th style={th(C.border)}>Status</th>
            <th style={th(C.border)}>Linked</th>
            <th style={th(C.border)}>Signup credit</th>
            <th style={th(C.border)}>Billing</th>
            <th style={th(C.border)}></th>
          </tr>
        </thead>
        <tbody>
          {props.rows.map((r) => {
            return (
              <tr key={r.link_id}>
                <td style={td(C.border)}>
                  <div style={{ fontWeight: 600 }}>{r.client_name}</div>
                  <div style={{ color: C.muted, fontFamily: monoFont, fontSize: 10 }}>
                    {r.client_account_id}
                  </div>
                  <div style={{ color: C.muted, fontSize: 11 }}>
                    {r.owner_email ?? '— no owner —'} · {r.primary_domain ?? 'no domain'}
                  </div>
                </td>
                <td style={td(C.border)}>{r.granted_role}</td>
                <td style={td(C.border)}>
                  <Pill tone={r.link_status === 'active' ? 'mint' : r.link_status === 'suspended' ? 'amber' : 'coral'}>
                    {r.link_status}
                  </Pill>
                  {r.revoked_by_role !== null ? (
                    <div style={{ color: C.muted, fontSize: 11, marginTop: 4 }}>
                      by {r.revoked_by_role}
                    </div>
                  ) : null}
                </td>
                <td style={td(C.border)}>{formatIsoCompact(r.created_at)}</td>
                <td style={td(C.border)}>
                  {r.is_agency_of_record ? (
                    <Pill tone="lime">Credited</Pill>
                  ) : (
                    <span style={{ color: C.muted }}>—</span>
                  )}
                </td>
                <td style={td(C.border)}>
                  {r.link_status === 'active' && r.agency_pays_for_sites ? (
                    <>
                      <Pill tone="mint">Agency pays</Pill>
                      <div style={{ color: C.muted, fontSize: 11, marginTop: 4 }}>
                        {r.client_live_sites} site{r.client_live_sites === 1 ? '' : 's'}
                      </div>
                    </>
                  ) : (
                    <Pill tone="neutral">Client pays</Pill>
                  )}
                </td>
                <td style={{ ...td(C.border), whiteSpace: 'nowrap' }}>
                  <ClientActions row={r} {...props} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    </>
  );
}

function ClientActions(props: ClientTableProps & { row: AdminAgencyManagedClient }): JSX.Element {
  const r = props.row;
  const label = r.primary_domain ?? r.client_name;
  return (
    <>
                  {props.historical !== true ? (
                    <>
                      <IconBtn onClick={() => props.onMove(r)} label="Move">
                        <Shuffle size={11} /> Move
                      </IconBtn>
                      <IconBtn onClick={() => props.onRemove(r)} label="Remove">
                        <UserMinus size={11} /> Remove
                      </IconBtn>
                      {r.agency_pays_for_sites ? (
                        <IconBtn
                          onClick={() => props.onAgencyPays(r, false)}
                          label="Client pays again"
                        >
                          <CircleDollarSign size={11} /> Client pays
                        </IconBtn>
                      ) : (
                        <IconBtn
                          onClick={() => props.onAgencyPays(r, true)}
                          label="Agency pays for this client's sites"
                        >
                          <CircleDollarSign size={11} /> Agency pays
                        </IconBtn>
                      )}
                    </>
                  ) : null}
                  {r.is_agency_of_record ? (
                    <IconBtn
                      onClick={() => props.onClearAor(r.client_account_id, label)}
                      label="Remove signup credit"
                    >
                      <Pin size={11} /> Remove credit
                    </IconBtn>
                  ) : (
                    <IconBtn
                      onClick={() => props.onSetAor(r.client_account_id, label)}
                      label="Credit this agency"
                    >
                      <Pin size={11} /> Credit this agency
                    </IconBtn>
                  )}
    </>
  );
}

function AttributedSignupsTable({
  rows,
}: {
  rows: readonly AdminAgencyAttributedSignup[];
}): JSX.Element {
  const C = useC();
  return (
    <>
    <div className="md:hidden">
      {rows.map((r) => (
        <div key={r.account_id} style={{ padding: '10px 0', borderBottom: `1px solid ${C.border}`, fontFamily: sansFont, fontSize: 12.5 }}>
          <div style={{ fontWeight: 600, color: C.text }}>{r.name}</div>
          <div style={{ color: C.muted, fontSize: 11, overflowWrap: 'anywhere' }}>
            {r.owner_email ?? '—'} · {r.primary_domain ?? 'no domain'}
          </div>
          <div style={{ color: C.muted, fontFamily: monoFont, fontSize: 10, overflowWrap: 'anywhere' }}>
            {r.account_id}
            {r.agency_of_record_set_at !== null ? ` · credited ${formatIsoCompact(r.agency_of_record_set_at)}` : ''}
          </div>
        </div>
      ))}
    </div>
    <div className="hidden md:block" style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', fontFamily: sansFont, fontSize: 12.5, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ color: C.muted, textAlign: 'left' }}>
            <th style={th(C.border)}>Account</th>
            <th style={th(C.border)}>Owner</th>
            <th style={th(C.border)}>Primary domain</th>
            <th style={th(C.border)}>Credited on</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.account_id}>
              <td style={td(C.border)}>
                <div style={{ fontWeight: 600 }}>{r.name}</div>
                <div style={{ color: C.muted, fontFamily: monoFont, fontSize: 10 }}>{r.account_id}</div>
              </td>
              <td style={td(C.border)}>{r.owner_email ?? '—'}</td>
              <td style={td(C.border)}>{r.primary_domain ?? '—'}</td>
              <td style={td(C.border)}>
                {r.agency_of_record_set_at !== null ? formatIsoCompact(r.agency_of_record_set_at) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </>
  );
}

// ─── Dialogs ────────────────────────────────────────────────────────

interface DialogShellProps {
  title: string;
  intro?: React.ReactNode;
  busy: boolean;
  disabled?: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: () => void;
  submitLabel: string;
  tone?: 'coral' | 'lime';
  children: React.ReactNode;
}

function DialogShell(props: DialogShellProps): JSX.Element {
  const C = useC();
  const tone = props.tone ?? 'lime';
  const submitBg = tone === 'coral' ? C.coralFg : C.lime;
  const submitFg = tone === 'coral' ? C.text : C.ink;
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 50,
      }}
      onClick={props.onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 3,
          padding: 20,
          width: '100%',
          maxWidth: 560,
          boxSizing: 'border-box',
          maxHeight: '90vh',
          overflowY: 'auto',
          fontFamily: sansFont,
        }}
      >
        <div style={{ fontFamily: serifFont, fontSize: 20, color: C.text, marginBottom: 8 }}>
          {props.title}
        </div>
        {props.intro !== undefined ? (
          <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 12, lineHeight: 1.5 }}>
            {props.intro}
          </div>
        ) : null}
        {props.children}
        {props.error !== null ? (
          <div
            style={{
              fontSize: 12.5,
              color: C.coralFg,
              marginTop: 8,
              padding: 8,
              border: `1px solid ${C.coralFg}`,
              borderRadius: 3,
            }}
          >
            {props.error}
          </div>
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button
            type="button"
            onClick={props.onCancel}
            disabled={props.busy}
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              padding: '8px 12px',
              background: 'transparent',
              color: C.text,
              border: `1px solid ${C.border}`,
              borderRadius: 3,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={props.onSubmit}
            disabled={props.busy || props.disabled === true}
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              padding: '8px 12px',
              background: submitBg,
              color: submitFg,
              border: `1px solid ${submitBg}`,
              borderRadius: 3,
              cursor: props.busy || props.disabled === true ? 'wait' : 'pointer',
              opacity: props.disabled === true ? 0.6 : 1,
            }}
          >
            {props.busy ? 'Working…' : props.submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

const ULID_RE = /^[0-9A-HJ-NP-TV-Z]{26}$/;

function AddClientDialog(props: {
  agency: AdminAgencyListItem;
  onClose: () => void;
  onDone: (msg: string) => void;
  onError: (msg: string) => void;
}): JSX.Element {
  const api = useAdminApi();
  const C = useC();
  const [client, setClient] = useState<AdminAccountSummary | null>(null);
  const clientAccountId = client?.account_id ?? '';
  const clientLabel = client === null ? '' : `${client.name} (${client.owner_email ?? client.account_id})`;
  const [grantedRole, setGrantedRole] = useState<AgencyGrantedRole>('admin');
  const [setAor, setSetAor] = useState(true);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const disabled =
    !ULID_RE.test(clientAccountId.trim())
    || reason.trim().length < 3
    || clientAccountId.trim() === props.agency.account_id;

  const onSubmit = useCallback(async (): Promise<void> => {
    if (!confirmed) {
      setConfirmed(true);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const r = await api.addAgencyClient(props.agency.account_id, {
        client_account_id: clientAccountId.trim(),
        granted_role: grantedRole,
        set_as_agency_of_record: setAor,
        reason: reason.trim(),
      });
      props.onDone(
        r.kind === 'already_active'
          ? `${clientLabel} is already a client of ${props.agency.name}.`
          : `Added ${clientLabel} as a client of ${props.agency.name}.`,
      );
    } catch (e) {
      const msg = e instanceof ApiError ? `${e.kind}: ${e.server_reason ?? e.message}` : 'Failed.';
      setErr(msg);
      props.onError(msg);
    } finally {
      setBusy(false);
    }
  }, [api, clientAccountId, clientLabel, confirmed, grantedRole, props, reason, setAor]);

  return (
    <DialogShell
      title="Add client to agency"
      intro={
        <>
          Gives <strong>{props.agency.name}</strong> access to a client account, as if the client had approved the agency&apos;s request. The client is not emailed; their audit log records that Sill staff added the agency.
        </>
      }
      busy={busy}
      error={err}
      onCancel={props.onClose}
      onSubmit={() => void onSubmit()}
      submitLabel={confirmed ? 'Confirm add' : 'Review'}
      disabled={disabled}
      tone="lime"
    >
      <Label>Client account</Label>
      <AccountPicker
        selected={client}
        onSelect={setClient}
        excludeAccountIds={[props.agency.account_id]}
        disabled={confirmed}
      />
      <Label>Role</Label>
      <select
        value={grantedRole}
        onChange={(e) => setGrantedRole(e.target.value as AgencyGrantedRole)}
        style={input(C)}
        disabled={confirmed}
      >
        {GRANTED_ROLES.map((r) => (
          <option key={r} value={r}>{r}</option>
        ))}
      </select>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 12.5 }}>
        <input
          type="checkbox"
          checked={setAor}
          onChange={(e) => setSetAor(e.target.checked)}
          disabled={confirmed}
        />
        Also credit this signup to {props.agency.name}, unless another agency already has the credit
      </label>
      <Label>Reason (saved in the staff log, at least 3 characters)</Label>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="e.g. Client confirmed assignment over email."
        rows={3}
        style={{ ...input(C), fontFamily: sansFont, resize: 'vertical' }}
      />
      {confirmed ? (
        <div style={{ marginTop: 10, fontSize: 12, color: C.muted }}>
          Confirm to give {props.agency.name} access to <strong>{clientLabel}</strong>.
        </div>
      ) : null}
    </DialogShell>
  );
}

function RemoveClientDialog(props: {
  agency_account_id: string;
  client: AdminAgencyManagedClient;
  onClose: () => void;
  onDone: (msg: string) => void;
  onError: (msg: string) => void;
}): JSX.Element {
  const api = useAdminApi();
  const C = useC();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const disabled = reason.trim().length < 3;

  const onSubmit = useCallback(async (): Promise<void> => {
    if (!confirmed) {
      setConfirmed(true);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.removeAgencyClient(props.agency_account_id, props.client.link_id, {
        reason: reason.trim(),
      });
      props.onDone(`Removed ${props.client.client_name} from the agency.`);
    } catch (e) {
      const msg = e instanceof ApiError ? `${e.kind}: ${e.server_reason ?? e.message}` : 'Failed.';
      setErr(msg);
      props.onError(msg);
    } finally {
      setBusy(false);
    }
  }, [api, confirmed, props, reason]);

  return (
    <DialogShell
      title={`Remove ${props.client.client_name}?`}
      intro={
        <>
          Writes <code>agency_link_revoked</code> with <code>revoked_by_role=&apos;ops&apos;</code> to the client&apos;s audit log. Any live acting session for this link will fail on the next request.
        </>
      }
      busy={busy}
      error={err}
      onCancel={props.onClose}
      onSubmit={() => void onSubmit()}
      submitLabel={confirmed ? 'Confirm remove' : 'Review'}
      disabled={disabled}
      tone="coral"
    >
      <Label>Reason (saved in the staff log, at least 3 characters)</Label>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="e.g. Client ended the engagement."
        rows={3}
        style={{ ...input(C), fontFamily: sansFont, resize: 'vertical' }}
      />
    </DialogShell>
  );
}

function MoveClientDialog(props: {
  from_agency_account_id: string;
  client: AdminAgencyManagedClient;
  agencies: readonly AdminAgencyListItem[];
  onClose: () => void;
  onDone: (msg: string) => void;
  onError: (msg: string) => void;
}): JSX.Element {
  const api = useAdminApi();
  const C = useC();
  const other_agencies = useMemo(
    () => props.agencies.filter((a) => a.account_id !== props.from_agency_account_id),
    [props.agencies, props.from_agency_account_id],
  );
  const [targetId, setTargetId] = useState(other_agencies[0]?.account_id ?? '');
  const [reason, setReason] = useState('');
  const [overrideThirdPartyAor, setOverrideThirdPartyAor] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  // Security-review Minor-1 (2026-10-29) — figure out if the client's
  // current AoR is a third-party agency (neither source nor target),
  // and if so require the checkbox before submit.
  const currentAorAccountId = props.client.client_agency_of_record_account_id;
  const isAorThirdParty =
    currentAorAccountId !== null
    && currentAorAccountId !== props.from_agency_account_id
    && currentAorAccountId !== targetId;
  const aorAgency = useMemo(
    () =>
      currentAorAccountId === null
        ? undefined
        : props.agencies.find((a) => a.account_id === currentAorAccountId),
    [currentAorAccountId, props.agencies],
  );

  const disabled =
    !ULID_RE.test(targetId)
    || reason.trim().length < 3
    || (isAorThirdParty && !overrideThirdPartyAor);

  const onSubmit = useCallback(async (): Promise<void> => {
    if (!confirmed) {
      setConfirmed(true);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const r = await api.moveAgencyClient(props.from_agency_account_id, props.client.link_id, {
        target_agency_account_id: targetId,
        reason: reason.trim(),
        ...(isAorThirdParty ? { override_third_party_aor: overrideThirdPartyAor } : {}),
      });
      // Security-review Minor-7 — honest message when the source was
      // already revoked.
      const msg = r.source_was_already_revoked
        ? `Source was already revoked; added ${props.client.client_name} to target.`
        : `Moved ${props.client.client_name}.`;
      props.onDone(msg);
    } catch (e) {
      const msg = e instanceof ApiError ? `${e.kind}: ${e.server_reason ?? e.message}` : 'Failed.';
      setErr(msg);
      props.onError(msg);
    } finally {
      setBusy(false);
    }
  }, [api, confirmed, isAorThirdParty, overrideThirdPartyAor, props, reason, targetId]);

  return (
    <DialogShell
      title={`Move ${props.client.client_name}`}
      intro={
        <>
          Removes this client from the current agency and adds it to the agency you pick, in one step. The signup credit moves to the new agency too. The client&apos;s audit log records the move.
        </>
      }
      busy={busy}
      error={err}
      onCancel={props.onClose}
      onSubmit={() => void onSubmit()}
      submitLabel={confirmed ? 'Confirm move' : 'Review'}
      disabled={disabled}
      tone="coral"
    >
      <Label>Signup currently credited to</Label>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 12.5,
          padding: '8px 10px',
          background: C.bg,
          border: `1px solid ${C.border}`,
          borderRadius: 3,
          color: C.text,
        }}
        data-testid="move-current-aor"
      >
        {currentAorAccountId === null ? (
          <span style={{ color: C.muted }}>— none —</span>
        ) : aorAgency !== undefined ? (
          <>
            {aorAgency.name}
            {' · '}
            <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
              {aorAgency.account_id}
            </span>
          </>
        ) : (
          <span style={{ fontFamily: monoFont, fontSize: 11 }}>{currentAorAccountId}</span>
        )}
      </div>

      <Label>Target agency</Label>
      <select
        value={targetId}
        onChange={(e) => setTargetId(e.target.value)}
        style={input(C)}
        disabled={confirmed}
      >
        {other_agencies.map((a) => (
          <option key={a.account_id} value={a.account_id}>
            {a.name} · {a.owner_email ?? 'no owner'} · {a.account_id}
          </option>
        ))}
      </select>

      {isAorThirdParty ? (
        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            marginTop: 12,
            padding: 10,
            fontSize: 12.5,
            background: C.coralDim,
            border: `1px solid ${C.coralFg}`,
            borderRadius: 3,
            color: C.text,
          }}
          data-testid="move-override-third-party"
        >
          <input
            type="checkbox"
            checked={overrideThirdPartyAor}
            onChange={(e) => setOverrideThirdPartyAor(e.target.checked)}
            disabled={confirmed}
            style={{ marginTop: 2 }}
          />
          <span>
            This signup is credited to a <strong>different</strong> agency ({aorAgency?.name ?? currentAorAccountId}). Move the credit to the new agency anyway.
          </span>
        </label>
      ) : null}

      <Label>Reason (saved in the staff log, at least 3 characters)</Label>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="e.g. Account manager handover; client confirmed by email."
        rows={3}
        style={{ ...input(C), fontFamily: sansFont, resize: 'vertical' }}
      />
    </DialogShell>
  );
}

function SetAorDialog(props: {
  agency_account_id: string;
  client_account_id: string;
  client_label: string;
  onClose: () => void;
  onDone: (msg: string) => void;
  onError: (msg: string) => void;
}): JSX.Element {
  const api = useAdminApi();
  const C = useC();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const disabled = reason.trim().length < 3;

  const onSubmit = useCallback(async (): Promise<void> => {
    if (!confirmed) {
      setConfirmed(true);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.setAgencyOfRecord(props.client_account_id, {
        agency_account_id: props.agency_account_id,
        reason: reason.trim(),
      });
      props.onDone(`Pinned ${props.client_label} to this agency.`);
    } catch (e) {
      const msg = e instanceof ApiError ? `${e.kind}: ${e.server_reason ?? e.message}` : 'Failed.';
      setErr(msg);
      props.onError(msg);
    } finally {
      setBusy(false);
    }
  }, [api, confirmed, props, reason]);

  return (
    <DialogShell
      title={`Credit ${props.client_label} to this agency`}
      intro={
        <>
          Credits this signup to this agency, replacing any agency credited before. It does not give the agency access to the account.
        </>
      }
      busy={busy}
      error={err}
      onCancel={props.onClose}
      onSubmit={() => void onSubmit()}
      submitLabel={confirmed ? 'Confirm credit' : 'Review'}
      disabled={disabled}
      tone="lime"
    >
      <Label>Reason (saved in the staff log, at least 3 characters)</Label>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="e.g. Account manager change confirmed in writing."
        rows={3}
        style={{ ...input(C), fontFamily: sansFont, resize: 'vertical' }}
      />
    </DialogShell>
  );
}

function ClearAorDialog(props: {
  client_account_id: string;
  client_label: string;
  onClose: () => void;
  onDone: (msg: string) => void;
  onError: (msg: string) => void;
}): JSX.Element {
  const api = useAdminApi();
  const C = useC();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const disabled = reason.trim().length < 3;

  const onSubmit = useCallback(async (): Promise<void> => {
    if (!confirmed) {
      setConfirmed(true);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.setAgencyOfRecord(props.client_account_id, {
        agency_account_id: null,
        reason: reason.trim(),
      });
      props.onDone(`Cleared AoR on ${props.client_label}.`);
    } catch (e) {
      const msg = e instanceof ApiError ? `${e.kind}: ${e.server_reason ?? e.message}` : 'Failed.';
      setErr(msg);
      props.onError(msg);
    } finally {
      setBusy(false);
    }
  }, [api, confirmed, props, reason]);

  return (
    <DialogShell
      title={`Remove signup credit: ${props.client_label}`}
      intro={
        <>Removes the signup credit from this account, so no agency is credited. Any agency access stays as it is.</>
      }
      busy={busy}
      error={err}
      onCancel={props.onClose}
      onSubmit={() => void onSubmit()}
      submitLabel={confirmed ? 'Confirm removal' : 'Review'}
      disabled={disabled}
      tone="coral"
    >
      <Label>Reason (saved in the staff log, at least 3 characters)</Label>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="e.g. Attribution reset per founder decision."
        rows={3}
        style={{ ...input(C), fontFamily: sansFont, resize: 'vertical' }}
      />
    </DialogShell>
  );
}

// ─── ADR-0263 §2.6 (S6) — Account plan summary ──────────────────────

function AccountPlanCard({
  account_plan,
}: {
  account_plan: AdminAgencyDetailResponse['account_plan'];
}): JSX.Element {
  const C = useC();
  const kicker = {
    fontFamily: monoFont,
    fontSize: 10,
    letterSpacing: 1.5,
    color: C.muted,
    textTransform: 'uppercase' as const,
  };
  if (account_plan === null) {
    return (
      <Card style={{ padding: 16, marginBottom: 12 }}>
        <div style={{ ...kicker, marginBottom: 8 }}>Account plan</div>
        <div style={{ fontFamily: sansFont, fontSize: 12.5, color: C.muted }}>
          No v2 account plan assigned. Agency pays switches do not resolve yet.
        </div>
      </Card>
    );
  }
  // ADR-0263 §2.6 / §5.3 — a NULL or non-positive `included_sites`
  // means zero slots (the resolver treats it as a mis-configured
  // plan, not unlimited). The UI label matches the resolver so staff
  // never think they can flip a client to agency-paid when no slots
  // exist (QA review 2026-11-02 MAJOR 1 / security MINOR 3).
  const effective_cap =
    account_plan.included_sites_cap === null
      || account_plan.included_sites_cap <= 0
      ? 0
      : account_plan.included_sites_cap;
  const cap_not_configured =
    account_plan.included_sites_cap === null
    || account_plan.included_sites_cap <= 0;
  const capLabel = cap_not_configured
    ? '0 (not configured)'
    : String(effective_cap);
  const over_cap = account_plan.included_sites_used > effective_cap;
  return (
    <Card style={{ padding: 16, marginBottom: 12 }}>
      <div style={{ ...kicker, marginBottom: 8 }}>Account plan</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: serifFont, fontSize: 20, color: C.text }}>
            {account_plan.name}
          </div>
          <div style={{ color: C.muted, fontFamily: monoFont, fontSize: 11, marginTop: 2 }}>
            {account_plan.slug}
          </div>
          {account_plan.included_site_plan_name === null ? (
            <div style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>
              No included-site plan on this account plan.
            </div>
          ) : (
            <div style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>
              Included sites run on <strong style={{ color: C.text }}>{account_plan.included_site_plan_name}</strong>.
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={kicker}>Included sites used</div>
          <div style={{ fontFamily: serifFont, fontSize: 22, color: over_cap ? C.coralFg : C.text }}>
            {account_plan.included_sites_used} / {capLabel}
          </div>
        </div>
      </div>
    </Card>
  );
}

// ─── ADR-0263 §5.3 (S6) — Agency-paid dialog ────────────────────────

function AgencyPaysDialog(props: {
  agency_account_id: string;
  client: AdminAgencyManagedClient;
  target: boolean;
  onClose: () => void;
  onDone: (msg: string) => void;
  onError: (msg: string) => void;
}): JSX.Element {
  const api = useAdminApi();
  const C = useC();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const disabled = reason.trim().length < 3;
  const label = props.client.primary_domain ?? props.client.client_name;

  const onSubmit = useCallback(async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      await api.setAgencyPaysForSites(props.agency_account_id, props.client.link_id, {
        agency_pays_for_sites: props.target,
        reason: reason.trim(),
      });
      props.onDone(
        props.target
          ? `Agency now pays for ${label}.`
          : `${label} is back on its own billing.`,
      );
    } catch (e) {
      const msg = e instanceof ApiError ? `${e.kind}: ${e.server_reason ?? e.message}` : 'Failed.';
      setErr(msg);
      props.onError(msg);
    } finally {
      setBusy(false);
    }
  }, [api, label, props, reason]);

  return (
    <DialogShell
      title={props.target ? `Agency pays for ${label}` : `Client pays again: ${label}`}
      intro={
        props.target ? (
          <>
            The client&apos;s sites will count against the agency plan&apos;s included sites,
            in site-created order across every agency-paid client. Sites beyond the cap
            fall back to the default plan.
          </>
        ) : (
          <>
            The client goes back to paying for its own sites. The link stays in place; only billing changes.
          </>
        )
      }
      busy={busy}
      error={err}
      onCancel={props.onClose}
      onSubmit={() => void onSubmit()}
      submitLabel={props.target ? 'Confirm agency pays' : 'Confirm client pays'}
      disabled={disabled}
      tone={props.target ? 'lime' : 'coral'}
    >
      <Label>Reason (saved in the staff log, at least 3 characters)</Label>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="e.g. New agency bundle takes over billing for this client."
        rows={3}
        style={{ ...input(C), fontFamily: sansFont, resize: 'vertical' }}
      />
    </DialogShell>
  );
}

// ─── Tiny helpers ───────────────────────────────────────────────────

function Label({ children }: { children: React.ReactNode }): JSX.Element {
  const C = useC();
  return (
    <div
      style={{
        fontFamily: monoFont,
        fontSize: 10,
        letterSpacing: 1.5,
        color: C.muted,
        textTransform: 'uppercase',
        marginTop: 10,
        marginBottom: 4,
      }}
    >
      {children}
    </div>
  );
}

function input(C: ReturnType<typeof useC>): React.CSSProperties {
  return {
    width: '100%',
    fontFamily: monoFont,
    fontSize: 12.5,
    padding: '8px 10px',
    background: C.bg,
    color: C.text,
    border: `1px solid ${C.border}`,
    borderRadius: 3,
    outline: 'none',
    boxSizing: 'border-box',
  };
}

function IconBtn(props: {
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  const C = useC();
  return (
    <button
      type="button"
      onClick={props.onClick}
      aria-label={props.label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontFamily: sansFont,
        fontSize: 11.5,
        padding: '4px 8px',
        background: 'transparent',
        color: C.text,
        border: `1px solid ${C.border}`,
        borderRadius: 3,
        cursor: 'pointer',
        marginRight: 6,
      }}
    >
      {props.children}
    </button>
  );
}

function th(border: string): React.CSSProperties {
  return {
    textAlign: 'left',
    padding: '6px 8px',
    borderBottom: `1px solid ${border}`,
    fontFamily: monoFont,
    fontSize: 10,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
  };
}

function td(border: string): React.CSSProperties {
  return {
    padding: '8px',
    borderBottom: `1px solid ${border}`,
    verticalAlign: 'top',
  };
}
